import { MoreVertical, Edit, XCircle, Trash2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { labelActions } from '@common-reducers/labels-reducer';
import { _Label } from '@common-models/label';
import { contactsLabelssSelectByQueryOnlyLength } from "@common-reducers/DBServiceThunks";
import { clearLabelChatsThunk, filterChatsByLabelIdThunk, deleteLabelThunk } from '@common-reducers/labels-reducer/labels-thunks';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from '@/lib/utils';
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState } from 'react';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { useConfirmDialog } from '@common/components/use-confirm-dialog';


type Props = {
    label: _Label
}

export default function LabelMoreOption({ label }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch()
    const { confirm, dialog: confirmDialog } = useConfirmDialog();

    const contactLabelsCount = useAppSelector((state) => contactsLabelssSelectByQueryOnlyLength(
        state.DBContactsLabelsReducer,
        { labelId: label._id }))

    // Portal logic derived from LabelsManagerButton
    const localPortalContainer = usePortalContainer();
    const [globalPortalContainer, setGlobalPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);

    useEffect(() => {
        // Try to find the global portal container (LabelsModals)
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setGlobalPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    const targetContainer = globalPortalContainer || localPortalContainer;

    // Check if actions disable
    // Rename should be allowed for all labels (user request)
    const isRenameDisabled = false;

    // Delete label is ONLY for custom labels
    const isDeleteLabelDisabled = label.type !== 'custom';

    // Disable clear if it's a system label that shouldn't be cleared, or if empty
    // System labels 'all' and 'unread' should not be cleared.
    const isSystemLabel = label.name === 'all' || label.name === 'unread';
    const isClearDisabled = isSystemLabel || !contactLabelsCount;

    return (
        <>
        {confirmDialog}
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors opacity-0 group-hover:opacity-100 outline-none data-[state=open]:opacity-100"
                >
                    <MoreVertical size={16} className="text-gray-500 dark:text-[var(--color-text-muted)]" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="center"
                className="min-w-[180px] z-[99999]"
                container={targetContainer}
            >
                <Tooltip
                    title={isRenameDisabled ? t('labelOptions.tooltips.renameDisabled', 'Default labels cannot be renamed') : ""}
                    arrow
                    placement="left"
                    slotProps={{ popper: { sx: { zIndex: 999999 } } }}
                >
                    <span className="block outline-none" tabIndex={-1}>
                        <DropdownMenuItem
                            disabled={isRenameDisabled}
                            onClick={(e) => {
                                if (!isRenameDisabled) {
                                    dispatch(labelActions.toggleEditLabel(true))
                                    dispatch(labelActions.setLabel(label))
                                }
                            }}
                            className="cursor-pointer"
                        >
                            <Edit size={16} className="mr-2" />
                            {t('labelOptions.rename', 'Rename')}
                        </DropdownMenuItem>
                    </span>
                </Tooltip>

                <Tooltip
                    title={isClearDisabled ? (isSystemLabel ? t('labelOptions.tooltips.clearSystemLabel', 'Cannot remove chats from this view') : t('labelOptions.tooltips.clearEmpty', 'No chats to remove')) : ""}
                    arrow
                    placement="left"
                    slotProps={{ popper: { sx: { zIndex: 999999 } } }}
                >
                    <span className="block outline-none" tabIndex={-1}>
                        <DropdownMenuItem
                            disabled={isClearDisabled}
                            onClick={(e) => {
                                if (!isClearDisabled) {
                                    if (label._id) dispatch(clearLabelChatsThunk(label._id))
                                }
                            }}
                            className={cn("cursor-pointer")}
                        >
                            <XCircle size={16} className="mr-2" />
                            {t('labelOptions.removeAllChats', 'Remove all chats from tab')}
                        </DropdownMenuItem>
                    </span>
                </Tooltip>

                {!isDeleteLabelDisabled && (
                    <DropdownMenuItem
                        onClick={async () => {
                            const ok = await confirm({
                                content: t('labelOptions.deleteConfirmation', 'Are you sure you want to delete this label?'),
                                title: t('labelOptions.deleteLabel', 'Delete Label'),
                                // MUI's Portal takes any DOM container (an element or a ShadowRoot); its typing only says Element.
                                container: targetContainer as Element,
                            });
                            if (ok) {
                                dispatch(labelActions.setLabel(label));
                                dispatch(deleteLabelThunk());
                            }
                        }}
                        className="cursor-pointer text-red-600 focus:text-red-700"
                    >
                        <Trash2 size={16} className="mr-2" />
                        {t('labelOptions.deleteLabel', 'Delete Label')}
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
        </>
    )
}
